import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { ArrowLeft, Check, Trash2, ZoomIn, ZoomOut } from "lucide-react";
import {
  getSigningRequest,
  getActiveSigningAssets,
  saveSigningPlacements,
  applySigning,
  fetchSigningPdfBlob,
  fetchSignatureAssetImageBlob,
} from "../../../api/hrm/signing/signingRequests";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Kích thước thực tế phổ biến (VN): con dấu công ty Ø36mm; chữ ký ~45×20mm */
const STAMP_DIAMETER_MM = 36;
const SIGNATURE_WIDTH_MM = 45;
const SIGNATURE_HEIGHT_MM = 20;
const A4_PTS = { width: 595.28, height: 841.89 };
const mmToPts = (mm) => (Number(mm) * 72) / 25.4;
const ptsToMm = (pts) => (Number(pts) * 25.4) / 72;

const defaultNormSize = (asset, pagePts = A4_PTS) => {
  const pw = pagePts.width || A4_PTS.width;
  const ph = pagePts.height || A4_PTS.height;
  if (asset?.asset_type === "stamp") {
    const d = mmToPts(STAMP_DIAMETER_MM);
    return { norm_width: d / pw, norm_height: d / ph };
  }
  return {
    norm_width: mmToPts(SIGNATURE_WIDTH_MM) / pw,
    norm_height: mmToPts(SIGNATURE_HEIGHT_MM) / ph,
  };
};

const placementSizeMm = (placement, pagePts = A4_PTS) => {
  const pw = pagePts.width || A4_PTS.width;
  const ph = pagePts.height || A4_PTS.height;
  return {
    widthMm: ptsToMm((placement?.norm_width || 0) * pw),
    heightMm: ptsToMm((placement?.norm_height || 0) * ph),
  };
};

/** Giữ hình tròn trên trang PDF: cạnh bằng nhau theo điểm (pt), không theo % */
const stampNormFromWidth = (normWidth, pagePts = A4_PTS) => {
  const pw = pagePts.width || A4_PTS.width;
  const ph = pagePts.height || A4_PTS.height;
  const sidePts = Math.max(mmToPts(12), Math.min(mmToPts(60), normWidth * pw));
  return {
    norm_width: sidePts / pw,
    norm_height: sidePts / ph,
  };
};

let placementKey = 0;
const nextKey = () => `p-${++placementKey}`;

const SigningPdfPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pageWrapRef = useRef(null);

  const [detail, setDetail] = useState(null);
  const [assets, setAssets] = useState([]);
  const [signerRole, setSignerRole] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pagePts, setPagePts] = useState(A4_PTS);
  const [pageWidth, setPageWidth] = useState(720);
  const [zoom, setZoom] = useState(1);
  const [placements, setPlacements] = useState([]);
  const [assetImages, setAssetImages] = useState({});
  const [selectedPlacementKey, setSelectedPlacementKey] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadImages = useCallback(async (assetList) => {
    const map = {};
    for (const a of assetList) {
      try {
        const buf = await fetchSignatureAssetImageBlob(a.id);
        const blob = new Blob([buf], { type: "image/png" });
        map[a.id] = URL.createObjectURL(blob);
      } catch {
        /* skip */
      }
    }
    setAssetImages((prev) => {
      Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
      return map;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [req, assetRes, pdfBuf] = await Promise.all([
          getSigningRequest(id),
          getActiveSigningAssets(id),
          fetchSigningPdfBlob(id, "original"),
        ]);
        if (cancelled) return;
        setDetail(req);
        setSignerRole(assetRes.signer_role || null);
        const activeAssets = assetRes.records || [];
        setAssets(activeAssets);
        await loadImages(activeAssets);

        const blob = new Blob([pdfBuf], { type: "application/pdf" });
        setPdfUrl(URL.createObjectURL(blob));

        if (req.placements?.length) {
          setPlacements(
            req.placements.map((p) => ({
              key: nextKey(),
              page_index: p.page_index,
              asset_id: p.asset_id,
              norm_x: Number(p.norm_x),
              norm_y: Number(p.norm_y),
              norm_width: Number(p.norm_width),
              norm_height: Number(p.norm_height),
            }))
          );
        }
      } catch {
        showErrorToast("Không tải được PDF.");
        navigate("/hrm/signing");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setPdfUrl((u) => {
        if (u) URL.revokeObjectURL(u);
        return null;
      });
    };
  }, [id, navigate, loadImages]);

  useEffect(() => {
    const onResize = () => {
      const w = pageWrapRef.current?.clientWidth || 720;
      setPageWidth(Math.min(920, Math.max(320, w - 32)));
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const addPlacement = (asset) => {
    const size = defaultNormSize(asset, pagePts);
    setPlacements((prev) => [
      ...prev,
      {
        key: nextKey(),
        page_index: 0,
        asset_id: asset.id,
        norm_x: 0.1,
        norm_y: 0.1,
        ...size,
      },
    ]);
    setSelectedPlacementKey(null);
  };

  const updatePlacement = (key, patch) => {
    setPlacements((prev) =>
      prev.map((p) => (p.key === key ? { ...p, ...patch } : p))
    );
  };

  const removePlacement = (key) => {
    setPlacements((prev) => prev.filter((p) => p.key !== key));
    setSelectedPlacementKey((prev) => (prev === key ? null : prev));
  };

  const handleDrag = (key, e, pageEl) => {
    if (!pageEl) return;
    const placement = placements.find((p) => p.key === key);
    const width = placement?.norm_width ?? 0.15;
    const height = placement?.norm_height ?? 0.1;
    const rect = pageEl.getBoundingClientRect();
    const norm_x = Math.max(0, Math.min(1 - width, (e.clientX - rect.left) / rect.width - width / 2));
    const norm_y = Math.max(0, Math.min(1 - height, (e.clientY - rect.top) / rect.height - height / 2));
    updatePlacement(key, { norm_x, norm_y });
  };

  const selectedPlacement = placements.find((p) => p.key === selectedPlacementKey) || null;
  const selectedAsset = assets.find((a) => a.id === selectedPlacement?.asset_id) || null;
  const selectedSizeMm = selectedPlacement
    ? placementSizeMm(selectedPlacement, pagePts)
    : null;
  const isStampSelected = selectedAsset?.asset_type === "stamp";

  const handleResizePlacement = (key, field, rawValue) => {
    const nextVal = Number(rawValue);
    const asset = assets.find(
      (a) => a.id === placements.find((p) => p.key === key)?.asset_id
    );
    setPlacements((prev) =>
      prev.map((p) => {
        if (p.key !== key) return p;
        if (asset?.asset_type === "stamp") {
          // Con dấu: luôn giữ cạnh bằng nhau theo mm (hình tròn trên giấy)
          const baseW = field === "norm_width" ? nextVal : p.norm_width;
          const sized = stampNormFromWidth(baseW, pagePts);
          return {
            ...p,
            ...sized,
            norm_x: Math.min(p.norm_x, 1 - sized.norm_width),
            norm_y: Math.min(p.norm_y, 1 - sized.norm_height),
          };
        }
        const next = { ...p, [field]: nextVal };
        next.norm_x = Math.min(next.norm_x, 1 - next.norm_width);
        next.norm_y = Math.min(next.norm_y, 1 - next.norm_height);
        return next;
      })
    );
  };

  const applyStandardSize = (key) => {
    const placement = placements.find((p) => p.key === key);
    const asset = assets.find((a) => a.id === placement?.asset_id);
    if (!placement || !asset) return;
    const sized = defaultNormSize(asset, pagePts);
    updatePlacement(key, {
      ...sized,
      norm_x: Math.min(placement.norm_x, 1 - sized.norm_width),
      norm_y: Math.min(placement.norm_y, 1 - sized.norm_height),
    });
  };

  const startResizePlacement = (e, placement) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPlacementKey(placement.key);
    setResizeState({
      key: placement.key,
      page_index: placement.page_index,
      asset_id: placement.asset_id,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: placement.norm_width,
      startHeight: placement.norm_height,
    });
  };

  useEffect(() => {
    if (!resizeState) return undefined;

    const handleMouseMove = (e) => {
      const pageEl = document.getElementById(`pdf-page-${resizeState.page_index}`);
      if (!pageEl) return;
      const rect = pageEl.getBoundingClientRect();
      const deltaX = (e.clientX - resizeState.startX) / rect.width;
      const deltaY = (e.clientY - resizeState.startY) / rect.height;
      const asset = assets.find((a) => a.id === resizeState.asset_id);

      setPlacements((prev) =>
        prev.map((p) => {
          if (p.key !== resizeState.key) return p;
          if (asset?.asset_type === "stamp") {
            const nextWidth = Math.max(0.06, Math.min(0.4, resizeState.startWidth + deltaX));
            const sized = stampNormFromWidth(nextWidth, pagePts);
            return {
              ...p,
              ...sized,
              norm_width: Math.min(sized.norm_width, 1 - p.norm_x),
              norm_height: Math.min(sized.norm_height, 1 - p.norm_y),
            };
          }
          const nextWidth = Math.max(0.08, Math.min(0.5, resizeState.startWidth + deltaX));
          const nextHeight = Math.max(0.04, Math.min(0.3, resizeState.startHeight + deltaY));
          return {
            ...p,
            norm_width: Math.min(nextWidth, 1 - p.norm_x),
            norm_height: Math.min(nextHeight, 1 - p.norm_y),
          };
        })
      );
    };

    const handleMouseUp = () => setResizeState(null);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizeState, assets, pagePts]);

  const handleConfirm = async () => {
    if (placements.length === 0) {
      showErrorToast("Hãy đặt ít nhất một chữ ký hoặc con dấu.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = placements.map(({ key, ...rest }) => rest);
      await saveSigningPlacements(id, payload);
      await applySigning(id);
      showSuccessToast("Đã ký văn bản thành công.");
      navigate("/hrm/signing");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi ký.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">Đang tải PDF...</div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/30 flex flex-col">
      <header className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20 shadow-sm">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate("/hrm/signing")}
            className="p-2 rounded-xl border border-gray-200 hover:border-red-300 hover:text-red-600 transition-colors shrink-0"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="font-black uppercase tracking-tight text-gray-800 text-sm sm:text-base truncate">
              {detail?.title}
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
              Kéo thả chữ ký / con dấu lên PDF
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden md:inline text-[11px] font-bold uppercase tracking-widest text-gray-400 mr-1">
            Zoom
          </span>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="hidden md:inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 px-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:border-red-300 hover:text-red-600"
          >
            Fit width
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="hidden md:inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 px-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:border-red-300 hover:text-red-600"
          >
            100%
          </button>
          <button
            type="button"
            onClick={() => setZoom(1.5)}
            className="hidden md:inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 px-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:border-red-300 hover:text-red-600"
          >
            150%
          </button>
          <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.15))} className="h-10 w-10 flex items-center justify-center rounded-xl border border-gray-200 hover:border-red-300 text-gray-500">
            <ZoomOut size={16} />
          </button>
          <span className="text-xs font-bold text-gray-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((z) => Math.min(2, z + 0.15))} className="h-10 w-10 flex items-center justify-center rounded-xl border border-gray-200 hover:border-red-300 text-gray-500">
            <ZoomIn size={16} />
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-700 to-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50 hover:shadow-lg hover:shadow-red-100 transition-all ml-2"
          >
            <Check size={16} />
            {submitting ? "Đang ký..." : "Xác nhận ký"}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-56 bg-white border-r border-gray-100 p-4 shrink-0 overflow-y-auto">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1">
            Thêm vào PDF
          </p>
          {signerRole ? (
            <p className="text-[10px] text-red-600 font-semibold mb-3">
              Đang ký với tư cách {signerRole}
            </p>
          ) : (
            <p className="text-[10px] text-gray-400 mb-3">Chữ ký / con dấu khả dụng</p>
          )}
          <div className="space-y-2">
            {assets.length === 0 ? (
              <p className="text-[11px] text-gray-400 italic">
                Không có chữ ký/con dấu phù hợp cho vai trò đang ký.
              </p>
            ) : (
              assets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => addPlacement(asset)}
                  className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-red-300 hover:bg-red-50/40 transition-all"
                >
                  {assetImages[asset.id] && (
                    <img src={assetImages[asset.id]} alt="" className="h-10 object-contain mb-1" />
                  )}
                  <p className="text-[10px] font-semibold">{asset.label}</p>
                  <p className="text-[9px] text-gray-400">
                    {asset.owner_role} · {asset.asset_type === "stamp" ? "Con dấu" : "Chữ ký"}
                    {asset.asset_type === "stamp" ? ` · Ø${STAMP_DIAMETER_MM}mm` : ""}
                  </p>
                </button>
              ))
            )}
          </div>

          {placements.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Đã đặt ({placements.length})</p>
              {placements.map((p, idx) => {
                const asset = assets.find((a) => a.id === p.asset_id);
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setSelectedPlacementKey(p.key)}
                    className={`w-full text-[10px] flex justify-between items-center py-2 px-2 rounded-lg border mb-1 transition-all ${
                      selectedPlacementKey === p.key
                        ? "border-red-200 bg-red-50/40 text-red-700"
                        : "border-transparent hover:border-gray-200 text-gray-600"
                    }`}
                  >
                    <span>#{idx + 1} Tr.{p.page_index + 1} {asset?.label?.slice(0, 12)}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        removePlacement(p.key);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          removePlacement(p.key);
                        }
                      }}
                      className="text-red-500"
                    >
                      <Trash2 size={12} />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selectedPlacement && (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50/60 p-3">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-3">
                Chỉnh kích thước
              </p>
              <p className="text-[10px] font-semibold text-gray-600 mb-1">
                {selectedAsset?.label || "Đối tượng đã chọn"}
              </p>
              {selectedSizeMm && (
                <p className="text-[10px] text-red-700 font-bold mb-3 tabular-nums">
                  ≈ {selectedSizeMm.widthMm.toFixed(1)} × {selectedSizeMm.heightMm.toFixed(1)} mm
                  {isStampSelected ? " (tròn)" : ""}
                </p>
              )}
              <p className="text-[9px] text-gray-400 mb-3 leading-relaxed">
                {isStampSelected
                  ? `Con dấu chuẩn VN thường Ø${STAMP_DIAMETER_MM}mm. Hệ thống giữ tỉ lệ tròn theo mm trên giấy.`
                  : `Chữ ký mặc định ~${SIGNATURE_WIDTH_MM}×${SIGNATURE_HEIGHT_MM}mm trên trang PDF.`}
              </p>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                    <span>{isStampSelected ? "Đường kính" : "Chiều ngang"}</span>
                    <span className="tabular-nums">
                      {selectedSizeMm
                        ? `${selectedSizeMm.widthMm.toFixed(1)} mm`
                        : `${Math.round(selectedPlacement.norm_width * 100)}%`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.06"
                    max="0.4"
                    step="0.005"
                    value={selectedPlacement.norm_width}
                    onChange={(e) =>
                      handleResizePlacement(
                        selectedPlacement.key,
                        "norm_width",
                        e.target.value
                      )
                    }
                    className="w-full accent-red-700"
                  />
                </div>

                {!isStampSelected && (
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                      <span>Chiều cao</span>
                      <span className="tabular-nums">
                        {selectedSizeMm
                          ? `${selectedSizeMm.heightMm.toFixed(1)} mm`
                          : `${Math.round(selectedPlacement.norm_height * 100)}%`}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.04"
                      max="0.3"
                      step="0.005"
                      value={selectedPlacement.norm_height}
                      onChange={(e) =>
                        handleResizePlacement(
                          selectedPlacement.key,
                          "norm_height",
                          e.target.value
                        )
                      }
                      className="w-full accent-red-700"
                    />
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => applyStandardSize(selectedPlacement.key)}
                  className="w-full h-9 rounded-xl border border-red-200 bg-white text-[10px] font-bold uppercase tracking-wider text-red-700 hover:bg-red-50 transition-colors"
                >
                  {isStampSelected
                    ? `Đặt Ø${STAMP_DIAMETER_MM}mm chuẩn`
                    : "Đặt kích thước chuẩn"}
                </button>
              </div>
            </div>
          )}
        </aside>

        <main ref={pageWrapRef} className="flex-1 overflow-y-auto p-4">
          {pdfUrl && (
            <Document
              file={pdfUrl}
              onLoadSuccess={async (pdf) => {
                setNumPages(pdf.numPages);
                try {
                  const page = await pdf.getPage(1);
                  const vp = page.getViewport({ scale: 1 });
                  if (vp?.width && vp?.height) {
                    setPagePts({ width: vp.width, height: vp.height });
                  }
                } catch {
                  setPagePts(A4_PTS);
                }
              }}
            >
              {Array.from({ length: numPages }, (_, pageIdx) => (
                <div key={pageIdx} className="relative mb-6 inline-block shadow-lg bg-white">
                  <div
                    className="relative"
                    id={`pdf-page-${pageIdx}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const assetId = e.dataTransfer.getData("assetId");
                      const key = e.dataTransfer.getData("placementKey");
                      const pageEl = document.getElementById(`pdf-page-${pageIdx}`);
                      if (key) {
                        handleDrag(key, e, pageEl);
                        updatePlacement(key, { page_index: pageIdx });
                      } else if (assetId) {
                        const asset = assets.find((a) => a.id === assetId);
                        const size = defaultNormSize(asset, pagePts);
                        const rect = pageEl.getBoundingClientRect();
                        setPlacements((prev) => [
                          ...prev,
                          {
                            key: nextKey(),
                            page_index: pageIdx,
                            asset_id: assetId,
                            norm_x: Math.max(
                              0,
                              (e.clientX - rect.left) / rect.width - size.norm_width / 2
                            ),
                            norm_y: Math.max(
                              0,
                              (e.clientY - rect.top) / rect.height - size.norm_height / 2
                            ),
                            ...size,
                          },
                        ]);
                      }
                    }}
                  >
                    <Page
                      pageNumber={pageIdx + 1}
                      width={pageWidth * zoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                    {placements
                      .filter((p) => p.page_index === pageIdx)
                      .map((p) => (
                        <div
                          key={p.key}
                          draggable
                          onClick={() => setSelectedPlacementKey(p.key)}
                          onDragStart={(e) => {
                            e.dataTransfer.setData("placementKey", p.key);
                          }}
                          onDragEnd={(e) => {
                            const pageEl = document.getElementById(`pdf-page-${pageIdx}`);
                            handleDrag(p.key, e, pageEl);
                          }}
                          className={`absolute border-2 cursor-move select-none rounded-sm ${
                            selectedPlacementKey === p.key
                              ? "border-red-600 bg-red-500/10 shadow-[0_0_0_2px_rgba(185,28,28,0.15)]"
                              : "border-red-400 bg-red-400/10"
                          }`}
                          style={{
                            left: `${p.norm_x * 100}%`,
                            top: `${p.norm_y * 100}%`,
                            width: `${p.norm_width * 100}%`,
                            height: `${p.norm_height * 100}%`,
                          }}
                        >
                          {assetImages[p.asset_id] && (
                            <img
                              src={assetImages[p.asset_id]}
                              alt=""
                              className="w-full h-full object-contain pointer-events-none"
                            />
                          )}
                          <button
                            type="button"
                            onMouseDown={(e) => startResizePlacement(e, p)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute -right-2 -bottom-2 h-4 w-4 rounded-full border border-red-600 bg-white shadow-sm cursor-se-resize"
                            title="Kéo để đổi kích thước"
                          >
                            <span className="block h-full w-full rounded-full bg-red-600/15" />
                          </button>
                        </div>
                      ))}
                  </div>
                  <p className="text-center text-xs text-gray-400 mt-1">Trang {pageIdx + 1}</p>
                </div>
              ))}
            </Document>
          )}
        </main>
      </div>
    </div>
  );
};

export default SigningPdfPage;
